/**
 * CreativeTemplatesPage — Phase 5 Surface 1 (now the sole template page).
 *
 * Renders the resolved CreativeTemplate canonicals (output of the Phase 5
 * backfill pipeline) at ``/creative-templates`` and supports deep-linking
 * via ``/creative-templates/:templateId`` so callers (PackView, AssetsLens)
 * can land on a specific template. Replaces the legacy AdTemplatesPage —
 * the backend ``/api/v1/ad-templates/...`` namespace stays since it serves
 * both ``creative-templates`` and the legacy ``format-templates`` endpoints
 * still consumed by AssetDetailModal and PackView.
 *
 * Features:
 *   - MacroCategoryRail: 8 macro chips + All
 *   - LayoutArchetypeFilter: closed-set dropdown with counts
 *   - ProvenanceFilter: image_grounded / research_hypothesis / human_curated
 *   - Template cards: display_name, layout pill, macro chip, source-ad
 *     count, clarity bar, ProvenanceBadge, hero image
 *   - Detail panel with Variants tab — pick hero exemplar
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  AlertCircle,
  CheckCircle2,
  ImageOff,
  Loader2,
  Search,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import { adTemplatesApi } from '@/services/api';
import type {
  CreativeTemplateResponse,
  CreativeTemplateVariantResponse,
  CreativeTemplateMacroCategory,
} from '@/services/api';

// ---------------------------------------------------------------------------
// Macro category configuration
// ---------------------------------------------------------------------------

const MACRO_CATEGORIES: Array<{
  value: CreativeTemplateMacroCategory | '';
  label: string;
  color: string;
}> = [
  { value: '', label: 'All', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'comparison', label: 'Comparison', color: 'bg-violet-100 text-violet-800 border-violet-300' },
  { value: 'testimonial', label: 'Testimonial', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'lifestyle_hero', label: 'Lifestyle Hero', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'product_focus', label: 'Product Focus', color: 'bg-sky-100 text-sky-800 border-sky-300' },
  { value: 'list_benefits', label: 'List Benefits', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  { value: 'offer_led', label: 'Offer Led', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  { value: 'educational', label: 'Educational', color: 'bg-teal-100 text-teal-800 border-teal-300' },
  { value: 'ugc_authentic', label: 'UGC Authentic', color: 'bg-orange-100 text-orange-800 border-orange-300' },
];

const PROVENANCE_OPTIONS: Array<{
  value: string;
  label: string;
  color: string;
}> = [
  { value: '', label: 'All', color: 'text-slate-700' },
  { value: 'image_grounded', label: 'Image-grounded', color: 'text-emerald-700' },
  { value: 'research_hypothesis', label: 'Research Hypothesis', color: 'text-amber-700' },
  { value: 'human_curated', label: 'Human Curated', color: 'text-sky-700' },
  { value: 'seed', label: 'Seed', color: 'text-slate-700' },
];

// ---------------------------------------------------------------------------
// Provenance + tier badges
// ---------------------------------------------------------------------------

function ProvenanceBadge({ provenance }: { provenance: string }) {
  const cfg: Record<string, { label: string; class: string }> = {
    image_grounded: { label: 'Image', class: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    research_hypothesis: { label: 'Hypothesis', class: 'bg-amber-100 text-amber-800 border-amber-200' },
    human_curated: { label: 'Curated', class: 'bg-sky-100 text-sky-800 border-sky-200' },
    seed: { label: 'Seed', class: 'bg-slate-100 text-slate-700 border-slate-200' },
    abstracted_from_cluster: { label: 'Abstract', class: 'bg-violet-100 text-violet-800 border-violet-200' },
  };
  const c = cfg[provenance] ?? cfg.seed;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium uppercase border',
        c.class,
      )}
    >
      {c.label}
    </span>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const cfg: Record<string, { label: string; class: string }> = {
    exemplar: { label: 'Exemplar', class: 'bg-amber-100 text-amber-800 border-amber-200' },
    reference: { label: 'Reference', class: 'bg-sky-100 text-sky-800 border-sky-200' },
    exploratory: { label: 'Exploratory', class: 'bg-slate-100 text-slate-600 border-slate-200' },
  };
  const c = cfg[tier] ?? cfg.reference;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium uppercase border',
        c.class,
      )}
    >
      <Star size={9} />
      {c.label}
    </span>
  );
}

function ClarityBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full',
            pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-slate-400',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] tabular-nums text-slate-500">{Math.round(pct)}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function CreativeTemplateCard({
  template,
  onClick,
  selected,
}: {
  template: CreativeTemplateResponse;
  onClick: () => void;
  selected: boolean;
}) {
  const [imgError, setImgError] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-col text-left bg-white border-2 rounded-xl overflow-hidden transition-all hover:shadow-md',
        selected ? 'border-navy-500 ring-2 ring-navy-200' : 'border-slate-200 hover:border-slate-300',
      )}
    >
      {/* Hero image */}
      <div className="relative w-full aspect-[4/3] bg-slate-100 flex items-center justify-center">
        {template.hero_image_url && !imgError ? (
          <img
            src={template.hero_image_url}
            alt={template.display_name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="text-slate-400 flex flex-col items-center gap-1 text-xs">
            <ImageOff size={20} />
            <span>{template.variant_count === 0 ? 'No image' : 'Failed to load'}</span>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          <ProvenanceBadge provenance={template.provenance} />
          <TierBadge tier={template.quality_tier} />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 p-3 flex flex-col gap-2">
        <div className="font-medium text-sm text-slate-900 leading-tight line-clamp-2">
          {template.display_name}
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-slate-100 text-slate-700 border border-slate-200">
            {template.layout_archetype}
          </span>
          {template.macro_category && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200">
              {template.macro_category.replace('_', ' ')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span>
            {template.variant_count} {template.variant_count === 1 ? 'source ad' : 'source ads'}
          </span>
        </div>

        <ClarityBar value={template.composition_clarity} />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function VariantThumb({
  variant,
  isHero,
  onSetHero,
}: {
  variant: CreativeTemplateVariantResponse;
  isHero: boolean;
  onSetHero: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      className={clsx(
        'flex flex-col bg-white rounded-lg overflow-hidden border-2',
        isHero ? 'border-navy-500 ring-2 ring-navy-200' : 'border-slate-200',
      )}
    >
      <div className="relative aspect-square bg-slate-100">
        {variant.image_url && !imgError ? (
          <img
            src={variant.image_url}
            alt={variant.variant_id}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <ImageOff size={20} />
          </div>
        )}
        {isHero && (
          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-navy-600 text-white text-[9px] font-bold uppercase">
            Hero
          </div>
        )}
      </div>
      <div className="p-2 flex flex-col gap-1">
        <div className="text-[10px] text-slate-500 font-mono truncate">{variant.variant_id.slice(0, 8)}</div>
        <div className="flex items-center gap-1">
          <ProvenanceBadge provenance={variant.provenance} />
        </div>
        <div className="flex items-center justify-between text-[10px] text-slate-600">
          <span>clarity</span>
          <span className="tabular-nums font-medium">
            {Math.round(variant.composition_clarity * 100)}%
          </span>
        </div>
        {!isHero && (
          <button
            type="button"
            onClick={onSetHero}
            className="text-[10px] py-1 px-2 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
          >
            Set as hero
          </button>
        )}
      </div>
    </div>
  );
}

function CreativeTemplateDetail({
  template,
  onClose,
  onUpdated,
}: {
  template: CreativeTemplateResponse;
  onClose: () => void;
  onUpdated: (updated: CreativeTemplateResponse) => void;
}) {
  const [tab, setTab] = useState<'overview' | 'variants'>('overview');
  const [busy, setBusy] = useState(false);

  const heroVariantId = useMemo(() => {
    if (!template.hero_image_url) return '';
    // Pick the variant whose image_url matches hero_image_url
    const match = template.variants.find((v) => v.image_url === template.hero_image_url);
    return match?.variant_id ?? template.variants[0]?.variant_id ?? '';
  }, [template]);

  async function setHero(variantId: string) {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await adTemplatesApi.updateCreativeTemplate(template.template_id, {
        hero_variant_id: variantId,
      });
      onUpdated(updated);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-[420px] flex-shrink-0 sticky top-4 bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1 flex-wrap">
            <ProvenanceBadge provenance={template.provenance} />
            <TierBadge tier={template.quality_tier} />
          </div>
          <div className="font-semibold text-base text-slate-900 leading-tight truncate">
            {template.display_name}
          </div>
          <div className="text-[11px] font-mono text-slate-500 mt-0.5 truncate">
            {template.canonical_name}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 ml-2"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-slate-50">
        {(['overview', 'variants'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'flex-1 py-2 text-xs font-medium uppercase tracking-wide',
              tab === t
                ? 'text-navy-700 border-b-2 border-navy-500 bg-white'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {t === 'overview' ? 'Overview' : `Variants (${template.variant_count})`}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="p-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
        {tab === 'overview' ? (
          <div className="flex flex-col gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Layout
              </div>
              <div className="text-slate-900">{template.layout_archetype}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Macro Category
              </div>
              <div className="text-slate-900">{template.macro_category}</div>
            </div>
            {template.aesthetic_tags.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Aesthetic Tags
                </div>
                <div className="flex flex-wrap gap-1">
                  {template.aesthetic_tags.map((t) => (
                    <span
                      key={t}
                      className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Composition Clarity
              </div>
              <ClarityBar value={template.composition_clarity} />
            </div>
            {template.lighting_preference && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Lighting
                </div>
                <div className="text-slate-900">{template.lighting_preference}</div>
              </div>
            )}
            {template.color_temperature && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Color Temperature
                </div>
                <div className="text-slate-900">{template.color_temperature}</div>
              </div>
            )}
            {template.do_not_use.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Do not use
                </div>
                <ul className="list-disc list-inside text-slate-700 text-[12px] space-y-0.5">
                  {template.do_not_use.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {(template.best_for_personas.length > 0 || template.best_for_scenarios.length > 0) && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                  Best for
                </div>
                <div className="flex flex-col gap-1 text-[12px]">
                  {template.best_for_personas.length > 0 && (
                    <div>
                      <span className="text-slate-500">Personas:</span>{' '}
                      <span className="text-slate-900">{template.best_for_personas.join(', ')}</span>
                    </div>
                  )}
                  {template.best_for_scenarios.length > 0 && (
                    <div>
                      <span className="text-slate-500">Scenarios:</span>{' '}
                      <span className="text-slate-900">{template.best_for_scenarios.join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {template.variants.map((v) => (
              <VariantThumb
                key={v.variant_id}
                variant={v}
                isHero={v.variant_id === heroVariantId}
                onSetHero={() => setHero(v.variant_id)}
              />
            ))}
            {busy && (
              <div className="col-span-2 flex items-center justify-center text-slate-400 text-xs gap-1 py-2">
                <Loader2 size={12} className="animate-spin" />
                Updating…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CreativeTemplatesPage() {
  // Deep-link param — supports `/creative-templates/:templateId` so callers
  // (PackView, AssetsLens, external links) can land on a specific template
  // and have it auto-selected once the catalog loads.
  const { templateId: routeTemplateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CreativeTemplateResponse[]>([]);
  const [counts, setCounts] = useState<{
    macro: Record<string, number>;
    layout: Record<string, number>;
    provenance: Record<string, number>;
  }>({ macro: {}, layout: {}, provenance: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [macroFilter, setMacroFilter] = useState<string>('');
  const [layoutFilter, setLayoutFilter] = useState<string>('');
  const [provenanceFilter, setProvenanceFilter] = useState<string>('');

  const [selected, setSelected] = useState<CreativeTemplateResponse | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const r = await adTemplatesApi.listCreativeTemplates({
        macro_category: macroFilter || undefined,
        layout_archetype: layoutFilter || undefined,
        provenance: provenanceFilter || undefined,
        search: search || undefined,
        limit: 200,
      });
      setData(r.templates);
      setCounts({
        macro: r.macro_category_counts || {},
        layout: r.layout_archetype_counts || {},
        provenance: r.provenance_counts || {},
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macroFilter, layoutFilter, provenanceFilter]);

  // Auto-select the template named in the URL once data is loaded.
  // Re-runs only when the route param or the data itself changes — avoids
  // clobbering manual selection. If the param doesn't resolve to anything
  // in the catalog the param is dropped silently (URL stays, no error).
  useEffect(() => {
    if (!routeTemplateId || data.length === 0) return;
    if (selected && selected.template_id === routeTemplateId) return;
    const match = data.find((t) => t.template_id === routeTemplateId);
    if (match) {
      setSelected(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTemplateId, data]);

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(
      (t) =>
        t.canonical_name.toLowerCase().includes(q) ||
        t.display_name.toLowerCase().includes(q),
    );
  }, [data, search]);

  // Refresh selected from data after PATCH so the detail panel reflects updates
  function handleUpdated(updated: CreativeTemplateResponse) {
    setData((rows) =>
      rows.map((r) => (r.template_id === updated.template_id ? updated : r)),
    );
    setSelected(updated);
  }

  const totalCount = filtered.length;
  const layoutOptions = useMemo(() => {
    return Object.entries(counts.layout)
      .sort((a, b) => b[1] - a[1])
      .map(([slug, count]) => ({ slug, count }));
  }, [counts.layout]);

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-navy-600" />
            <h1 className="text-xl font-semibold text-slate-900">Creative Templates</h1>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-navy-100 text-navy-800 border border-navy-200">
              Phase 5
            </span>
          </div>
          <p className="text-sm text-slate-500">
            Resolved canonical creative concepts — rich-named templates with layout
            archetype as a facet, source-ad variants, and provenance badges.
            Published copies live on the{' '}
            <Link to="/knowledge/creative-strategy/library/global-templates" className="text-navy-700 underline">
              global Library
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Macro category rail */}
      <div className="flex items-center gap-1 mb-3 flex-wrap">
        {MACRO_CATEGORIES.map((m) => {
          const count = m.value === '' ? totalCount : counts.macro[m.value] || 0;
          return (
            <button
              key={m.value}
              type="button"
              onClick={() => setMacroFilter(m.value)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all',
                macroFilter === m.value
                  ? clsx(m.color, 'shadow-sm')
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300',
              )}
            >
              {m.label}
              <span
                className={clsx(
                  'tabular-nums',
                  macroFilter === m.value ? 'opacity-80' : 'text-slate-400',
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar — search + secondary filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search canonical or display name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:border-navy-400"
          />
        </div>
        <select
          value={layoutFilter}
          onChange={(e) => setLayoutFilter(e.target.value)}
          className="text-sm rounded-lg border border-slate-200 px-3 py-2"
        >
          <option value="">All layouts</option>
          {layoutOptions.map(({ slug, count }) => (
            <option key={slug} value={slug}>
              {slug} ({count})
            </option>
          ))}
        </select>
        <select
          value={provenanceFilter}
          onChange={(e) => setProvenanceFilter(e.target.value)}
          className="text-sm rounded-lg border border-slate-200 px-3 py-2"
        >
          {PROVENANCE_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
              {p.value && counts.provenance[p.value]
                ? ` (${counts.provenance[p.value]})`
                : ''}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      {/* Grid + sticky detail */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-2 text-slate-400 text-sm">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
              <CheckCircle2 size={24} />
              <span>No creative templates match the current filters.</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-6">
              {filtered.map((t) => (
                <CreativeTemplateCard
                  key={t.template_id}
                  template={t}
                  onClick={() => {
                    setSelected(t);
                    navigate(`/creative-templates/${t.template_id}`);
                  }}
                  selected={selected?.template_id === t.template_id}
                />
              ))}
            </div>
          )}
        </div>
        {selected && (
          <CreativeTemplateDetail
            template={selected}
            onClose={() => {
              setSelected(null);
              navigate('/creative-templates');
            }}
            onUpdated={handleUpdated}
          />
        )}
      </div>
    </div>
  );
}

export default CreativeTemplatesPage;
